/**
 * Copyright (c) 2024 TechDivision GmbH
 * All rights reserved
 *
 * This product includes proprietary software developed at TechDivision GmbH, Germany
 * For more information see https://www.techdivision.com/
 *
 * To obtain a valid license for using this software please contact us at
 * license@techdivision.com
 *
 * @author Florian Hartl <f.hartl@techdivision.com>
 */

import React from 'react';
import { Grid } from '@mui/material';
import ErrorBoundary from '../Errors/ErrorBoundary';
import { ErrorMessage } from '../Errors/ErrorMessage';
import { useStoreAppFrame } from './useStoreAppFrame';
import { FullScreenLoadingOverlay } from '../Components/LoadingOverlay/FullScreenLoadingOverlay';
import ErrorTemplate from '../Errors/ErrorTemplate';
import { StudioForm } from './Checkout/Components/StudioForm';
import { ContractForm } from './Checkout/Components/ContractForm';
import { ContactForm } from './Checkout/Components/ContactForm';
import { PaymentForm } from './Checkout/Components/PaymentForm';
import { PreviewForm } from './Checkout/Components/PreviewForm';
import Checkout from './Checkout';
import { checkoutFormModel } from './Checkout/FormModel/FormModel';

const { formField } = checkoutFormModel;

/**
 * StoreAppFrame Component
 *
 * This AppFrame stores query parameter and data attributes to the session storage before the actual component is rendered
 *
 * @returns {JSX.Element}
 * @constructor
 */
function StoreAppFrame() {
  const checkoutParams = {
    stepData: [{
      label: 'formStudioStepLabel',
      component: <StudioForm formField={formField} />,
      nextLabel: 'nextStepBtnLabel',
      previousLabel: 'backStepBtnLabel',
    }, {
      label: 'formContractStepLabel',
      component: <ContractForm formField={formField} />,
      nextLabel: 'nextStepBtnLabel',
      previousLabel: 'backStepBtnLabel',
    }, {
      label: 'formContactStepLabel',
      component: <ContactForm formField={formField} />,
      nextLabel: 'nextStepBtnLabel',
      previousLabel: 'backStepBtnLabel',
    }, {
      label: 'formPaymentStepLabel',
      component: <PaymentForm formField={formField} />,
      nextLabel: 'nextStepBtnLabel',
      previousLabel: 'backStepBtnLabel',
    }, {
      label: 'formValidationStepLabel',
      component: <PreviewForm formField={formField} />,
      nextLabel: 'submitContractBtnLabel',
      previousLabel: 'backStepBtnLabel',
    }],
  };

  const {
    appFrameQueryParametersInitialized,
    appFrameDataAttributesInitialized,
  } = useStoreAppFrame();

  if (!appFrameQueryParametersInitialized && !appFrameDataAttributesInitialized) {
    return <FullScreenLoadingOverlay />;
  }

  return (
    <Grid container direction="row">
      <Grid
        item
        xs
        p="16px 0 80px 0"
        sx={{
          overflowY: 'auto',
          flex: 1,
        }}
      >
        <ErrorBoundary fallback={<ErrorTemplate />}>
          <React.Suspense fallback={<div>Loading...</div>}>
            <Checkout stepData={checkoutParams.stepData} />
          </React.Suspense>
          <ErrorMessage />
        </ErrorBoundary>
      </Grid>
    </Grid>
  );
}

export default StoreAppFrame;
